/**
 * <announcement-bar> web component.
 * Marquee animation is pure CSS — JS only handles:
 *   • Measuring one message cycle and adding enough hidden-from-AT repeats for continuous coverage
 *   • Sticky-bar height tracking (CSS variable for downstream sticky offsets)
 *   • Moving links + localization between the desktop bar and mobile menu on breakpoint change
 */

if (!customElements.get('announcement-bar')) {
  class AnnouncementBar extends HTMLElement {
    constructor() {
      super();
      this.localization = this.querySelector('.announcement__localization');
      this.links = this.querySelectorAll('.js-announcement-link');
      this.menu = document.querySelector('.main-menu__content');
      this.isSticky = this.dataset.isSticky === 'true';
      this.boundFontLoad = this.scheduleMarqueeLayout.bind(this);
    }

    connectedCallback() {
      this.marquee = this.querySelector('.announcement__col--left.has-marquee .announcement__marquee');
      this.track = this.marquee?.querySelector('.announcement__track');
      this.sourceGroup = this.track?.querySelector('.announcement__group:not([aria-hidden="true"])');
      this.cloneTemplate = this.track?.querySelector('.announcement__group[aria-hidden="true"]');
      // The Liquid-rendered hidden template group carries the same focusables
      // as its clones - park them here too so keyboard users never land in a
      // hidden copy (clones inherit this via cloneNode).
      this.cloneTemplate?.querySelectorAll('a, button, [tabindex]').forEach((el) => {
        el.setAttribute('tabindex', '-1');
      });

      if (this.isSticky) {
        this.closest('.cc-announcement')?.classList.add('announcement--sticky');
      }

      this.updateHeight();
      this.resizeObserver = new ResizeObserver(() => {
        this.updateHeight();
        this.scheduleMarqueeLayout();
      });
      this.resizeObserver.observe(this);
      if (this.marquee) this.resizeObserver.observe(this.marquee);
      if (this.sourceGroup) this.resizeObserver.observe(this.sourceGroup);

      this.scheduleMarqueeLayout();
      if (this.track && document.fonts) {
        document.fonts.addEventListener?.('loadingdone', this.boundFontLoad);
        document.fonts.ready.then(this.boundFontLoad, this.boundFontLoad);
      }

      if (this.links.length) {
        this.boundMoveLinks = this.moveLinks.bind(this);
        window.addEventListener('on:breakpoint-change', this.boundMoveLinks);
        this.moveLinks();
      }

      if (this.localization) {
        this.boundMoveLocalization = this.moveLocalization.bind(this);
        window.addEventListener('on:breakpoint-change', this.boundMoveLocalization);
        this.moveLocalization();
      }
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      if (this.marqueeFrame) cancelAnimationFrame(this.marqueeFrame);
      document.fonts?.removeEventListener?.('loadingdone', this.boundFontLoad);
      this.removeGeneratedMarqueeGroups();
      this.track?.style.removeProperty('--announcement-marquee-distance');
      if (this.boundMoveLinks) {
        window.removeEventListener('on:breakpoint-change', this.boundMoveLinks);
      }
      if (this.boundMoveLocalization) {
        window.removeEventListener('on:breakpoint-change', this.boundMoveLocalization);
      }
    }

    scheduleMarqueeLayout() {
      if (!this.isConnected || !this.marquee || !this.track || !this.sourceGroup) return;
      if (this.marqueeFrame) cancelAnimationFrame(this.marqueeFrame);
      this.marqueeFrame = requestAnimationFrame(() => {
        this.marqueeFrame = null;
        this.layoutMarquee();
      });
    }

    removeGeneratedMarqueeGroups() {
      this.track?.querySelectorAll('[data-announcement-clone]').forEach((group) => group.remove());
    }

    layoutMarquee() {
      this.removeGeneratedMarqueeGroups();

      const cycleWidth = this.sourceGroup.getBoundingClientRect().width;
      const marqueeWidth = this.marquee.clientWidth;
      if (!cycleWidth || !marqueeWidth || !this.cloneTemplate) return;

      this.track.style.setProperty('--announcement-marquee-distance', `${-cycleWidth}px`);

      const minimumTrackWidth = Math.max(marqueeWidth * 2, marqueeWidth + cycleWidth) + 1;
      const baselineGroupCount = this.track.querySelectorAll('.announcement__group').length;
      const requiredGroupCount = Math.max(2, Math.ceil(minimumTrackWidth / cycleWidth));

      for (let index = baselineGroupCount; index < requiredGroupCount; index += 1) {
        const clone = this.cloneTemplate.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        // aria-hidden removes the clones from the accessibility tree but not
        // the tab order - park every focusable so keyboard users only meet
        // the first (visible) copy of each link.
        clone.querySelectorAll('a, button, [tabindex]').forEach((el) => {
          el.setAttribute('tabindex', '-1');
        });
        clone.dataset.announcementClone = '';
        this.track.appendChild(clone);
      }
    }

    /** Update the global --announcement-bar-height var so sticky page elements offset correctly. */
    updateHeight() {
      const value = this.isSticky ? `${this.offsetHeight}px` : '0px';
      document.body.style.setProperty('--announcement-bar-height', value);
    }

    /** Relocate announcement links into the mobile nav at small breakpoints; restore on desktop. */
    moveLinks() {
      if (!this.menu) return;
      const existing = document.querySelector('.mob__announcement-links');
      const isDesktop = window.theme?.mediaMatches?.md;

      if (!isDesktop && !existing && this.links.length) {
        const nav = document.createElement('nav');
        nav.classList.add('mob__announcement-links', 'secondary-nav');
        this.links.forEach((link) => {
          const li = document.createElement('li');
          const a = document.createElement('a');
          a.className = 'secondary-nav__item';
          a.href = link.href;
          a.textContent = link.innerText;
          li.appendChild(a);
          nav.appendChild(li);
        });
        this.menu.appendChild(nav);
      } else if (isDesktop && existing) {
        existing.remove();
      }
    }

    /** Relocate localization controls into the mobile nav at small breakpoints; restore on desktop. */
    moveLocalization() {
      if (!this.menu || !this.localization) return;
      const existing = document.querySelector('.mob__localization');
      const isDesktop = window.theme?.mediaMatches?.md;

      if (!isDesktop && !existing && this.localization.firstElementChild) {
        const wrapper = document.createElement('div');
        wrapper.classList.add('mob__localization');
        wrapper.appendChild(this.localization.firstElementChild);
        this.menu.appendChild(wrapper);
      } else if (isDesktop && existing) {
        this.localization.appendChild(existing.firstElementChild);
        existing.remove();
      }
    }
  }

  customElements.define('announcement-bar', AnnouncementBar);
}
